import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save, ArrowLeft, Plus, Trash2 } from "lucide-react";
import { useReceiptBanking, useBankAccount, useCustomer } from "hooks";
import { ReceiptBanking, ReceiptBankingForm, ReceiptBankingItem } from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import { ROUTES } from "utils";
import { LoadingState, InputNumber } from "components";
import { sanitizeReceiptBankingItems } from "utils/helpers/sanitizers";
import { normalizeDateInput } from "components/ui-common/AgGridTable/DateCellEditor";

const todayString = () => new Date().toISOString().slice(0, 10);

const defaultForm: ReceiptBankingForm = {
  receipt_date: todayString(),
  bank_account_id: undefined,
  bank_name: "",
  transaction_person: "",
  payer_name: "",
  address: "",
  description: "",
  attachments: [],
  export_invoice_id: undefined,
  items: [],
};

const defaultItem: ReceiptBankingItem = {
  amount: 0,
  partner_id: undefined,
  reason: "",
  business_field: "",
  description: "",
  vat_rate: undefined,
  vat_amount: undefined,
  pit_rate: undefined,
  pit_amount: undefined,
};

const parseNumber = (value: unknown): number => {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseId = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  const parsed = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const sanitizeItems = (items?: ReceiptBankingItem[]): ReceiptBankingItem[] =>
  sanitizeReceiptBankingItems(items);

const ATTACHMENT_SPLIT_REGEX = /[,\n]/;

const normalizeAttachments = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item : String(item ?? "")))
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => (typeof item === "string" ? item : String(item ?? "")))
          .map((item) => item.trim())
          .filter(Boolean);
      }
    } catch {
      // ignore invalid JSON
    }

    return trimmed
      .split(ATTACHMENT_SPLIT_REGEX)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const ReceiptBankingDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { fetchDetail, createReceiptBanking, updateReceiptBanking } =
    useReceiptBanking();
  const {
    data: detailResponse,
    isFetching: isFetchingDetail,
    isLoading: isLoadingDetail,
    isError: isDetailError,
  } = fetchDetail(id ?? "", isEdit);

  const {
    data: bankAccountsData,
    isFetching: isFetchingBankAccounts,
    isLoading: isLoadingBankAccounts,
    isError: isBankAccountsError,
  } = useBankAccount().fetchList({ page: 1, pageSize: 200 }, true);
  const {
    data: customersData,
    isFetching: isFetchingCustomers,
    isLoading: isLoadingCustomers,
    isError: isCustomersError,
  } = useCustomer().fetchList({ page: 1, pageSize: 200 }, true);

  const [form, setForm] = useState<ReceiptBankingForm>(defaultForm);
  const [items, setItems] = useState<ReceiptBankingItem[]>([defaultItem]);
  const [attachmentsText, setAttachmentsText] = useState<string>("");

  useEffect(() => {
    if (!detailResponse?.data) return;
    const detail = detailResponse.data as ReceiptBanking;
    const normalizedItems = sanitizeItems(detail.items);
    const normalizedAttachments = normalizeAttachments(detail.attachments);
    setForm({
      receipt_date: normalizeDateInput(detail.receipt_date),
      bank_account_id: parseId(detail.bank_account_id),
      bank_name: detail.bank_name || "",
      transaction_person: detail.transaction_person || "",
      payer_name: detail.payer_name || "",
      address: detail.address || "",
      description: detail.description || "",
      attachments: normalizedAttachments,
      export_invoice_id: parseId(detail.export_invoice_id),
      items: normalizedItems,
    });
    setItems(normalizedItems.length ? normalizedItems : [defaultItem]);
    setAttachmentsText(normalizedAttachments.join(", "));
  }, [detailResponse]);

  useEffect(() => {
    if (isEdit) return;
    setForm(defaultForm);
    setItems([defaultItem]);
    setAttachmentsText("");
  }, [isEdit]);

  useEffect(() => {
    if (isDetailError || isBankAccountsError || isCustomersError) {
      SnackbarUtils.error("Không thể tải dữ liệu phiếu thu ngân hàng");
      navigate(ROUTES.ACCOUNTING.BANK_RECEIPT);
    }
  }, [
    isBankAccountsError,
    isCustomersError,
    isDetailError,
    navigate,
  ]);

  const totalAmount = useMemo(
    () => items.reduce((sum, item) => sum + (Number(item.amount ?? 0) || 0), 0),
    [items],
  );

  const queriesPending =
    isLoadingDetail ||
    isLoadingBankAccounts ||
    isLoadingCustomers ||
    isFetchingDetail ||
    isFetchingBankAccounts ||
    isFetchingCustomers;
  const isMissingData =
    !bankAccountsData || !customersData || (isEdit && !detailResponse);
  const isSaving =
    createReceiptBanking.isPending || updateReceiptBanking.isPending;
  const showLoading = isSaving || (queriesPending && isMissingData);

  const handleFormChange = useCallback(
    (key: keyof ReceiptBankingForm, value: unknown) => {
      setForm((prev) => ({
        ...prev,
        [key]:
          key === "bank_account_id" || key === "export_invoice_id"
            ? parseId(value)
            : typeof value === "string"
              ? value
              : (value ?? ""),
      }));
    },
    [],
  );

  const handleAddItem = useCallback(() => {
    setItems((prev) => [...prev, { ...defaultItem }]);
  }, []);

  const handleRemoveItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleItemChange = useCallback(
    (index: number, key: keyof ReceiptBankingItem, value: unknown) => {
      setItems((prev) =>
        prev.map((item, i) =>
          i === index
            ? {
                ...item,
                [key]:
                  key === "amount" ||
                  key === "vat_rate" ||
                  key === "vat_amount" ||
                  key === "pit_amount"
                    ? parseNumber(value)
                  : key === "partner_id"
                    ? parseId(value)
                      : typeof value === "string"
                        ? value
                        : value,
              }
            : item,
        ),
      );
    },
    [],
  );

  const handleSubmit = useCallback(() => {
    const payload: ReceiptBankingForm = {
      ...form,
      bank_account_id: parseId(form.bank_account_id),
      export_invoice_id: parseId(form.export_invoice_id),
      attachments: normalizeAttachments(attachmentsText),
      items: sanitizeItems(items),
    };

    if (!payload.receipt_date || !payload.bank_account_id) {
      SnackbarUtils.error("Ngày thu và Tài khoản ngân hàng là bắt buộc");
      return;
    }
    if (!payload.items?.length || !payload.items[0].amount) {
      SnackbarUtils.error("Vui lòng nhập số tiền thu");
      return;
    }

    if (isEdit && id) {
      updateReceiptBanking.mutate(
        { id, ...payload },
        {
          onSuccess: () => navigate(ROUTES.ACCOUNTING.BANK_RECEIPT),
        },
      );
      return;
    }

    createReceiptBanking.mutate(payload, {
      onSuccess: () => navigate(ROUTES.ACCOUNTING.BANK_RECEIPT),
    });
  }, [
    attachmentsText,
    createReceiptBanking,
    form,
    id,
    isEdit,
    items,
    navigate,
    updateReceiptBanking,
  ]);

  const bankAccountOptions =
    bankAccountsData?.data?.map((b) => ({
      value: b.id,
      label: `${b.bank_name ?? ""}${b.account_number ? ` - ${b.account_number}` : ""}`,
    })) ?? [];

  const customerOptions =
    (customersData?.data as any[])?.map((c) => ({
      value: c.id,
      label: `${c.code}${c.name ? ` - ${c.name}` : ""}`,
    })) ?? [];

  return (
    <>
      {showLoading && (
        <LoadingState
          message="Đang đồng bộ dữ liệu"
          description="Vui lòng chờ trong khi hệ thống tải hoặc lưu phiếu thu ngân hàng."
        />
      )}
      <div className="relative p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {isEdit ? "Cập nhật" : "Tạo"} phiếu thu ngân hàng
            </h1>
            <p className="text-sm text-gray-600">
              Nhập thông tin chứng từ thu tiền chuyển khoản và chi tiết dòng thu.
            </p>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate(ROUTES.ACCOUNTING.BANK_RECEIPT)}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4" />
              Quay lại danh sách
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-70"
              disabled={isSaving}
            >
              <Save className="h-4 w-4" />
              {isSaving
                ? "Đang lưu..."
                : isEdit
                  ? "Lưu thay đổi"
                  : "Tạo phiếu thu"}
            </button>
          </div>
        </div>

      <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Ngày thu
            </label>
            <input
              type="date"
              value={form.receipt_date ?? ""}
              onChange={(e) => handleFormChange("receipt_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Tài khoản ngân hàng
            </label>
            <select
              value={form.bank_account_id ?? ""}
              onChange={(e) =>
                handleFormChange("bank_account_id", e.target.value)
              }
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            >
              <option value="">Chọn tài khoản</option>
              {bankAccountOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Người giao dịch
            </label>
            <input
              value={form.transaction_person ?? ""}
              onChange={(e) =>
                handleFormChange("transaction_person", e.target.value)
              }
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Nguyễn Văn A"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Người nộp
            </label>
            <input
              value={form.payer_name ?? ""}
              onChange={(e) => handleFormChange("payer_name", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Nguyễn Văn B"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Địa chỉ</label>
            <input
              value={form.address ?? ""}
              onChange={(e) => handleFormChange("address", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="123 Đường ABC, Quận 1"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Hóa đơn xuất (ID)
            </label>
            <input
              type="number"
              value={form.export_invoice_id ?? ""}
              onChange={(e) =>
                handleFormChange("export_invoice_id", e.target.value)
              }
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="1"
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <label className="text-sm font-medium text-gray-700">Ghi chú</label>
            <textarea
              value={form.description ?? ""}
              onChange={(e) => handleFormChange("description", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              rows={3}
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <label className="text-sm font-medium text-gray-700">
              Đính kèm (chuỗi hoặc danh sách link, ngăn cách bởi dấu phẩy/dòng)
            </label>
            <textarea
              value={attachmentsText}
              onChange={(e) => setAttachmentsText(e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              rows={2}
            />
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Chi tiết thu
            </h3>
            <p className="text-xs text-gray-500">
              Nhập số tiền thu và thông tin liên quan.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xs text-gray-500">Tổng tiền</p>
              <p className="text-base font-semibold text-gray-900">
                {totalAmount.toLocaleString("vi-VN")} đ
              </p>
            </div>
            <button
              type="button"
              onClick={handleAddItem}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              <Plus className="h-4 w-4 text-green-700" />
              Thêm dòng
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[900px] w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">STT</th>
                <th className="px-3 py-2 text-right font-semibold">
                  Số tiền
                </th>
                <th className="px-3 py-2 text-left font-semibold">Đối tác</th>
                <th className="px-3 py-2 text-left font-semibold">
                  Lý do thu
                </th>
                <th className="px-3 py-2 text-left font-semibold">Mô tả</th>
                <th className="px-3 py-2 text-right font-semibold">VAT %</th>
                <th className="px-3 py-2 text-center font-semibold">
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {items.length ? (
                items.map((item, index) => (
                  <tr key={`item-${index}`} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                    <td className="px-3 py-2">
                      <InputNumber
                        value={item.amount ?? 0}
                        onValueChange={(values) =>
                          handleItemChange(
                            index,
                            "amount",
                            values.floatValue ?? 0,
                          )
                        }
                        className="h-9 px-2 py-1 text-right text-sm"
                        suffix=" ₫"
                      />
                    </td>
                    <td className="px-3 py-2 text-gray-900">
                      <select
                        value={item.partner_id ?? ""}
                        onChange={(e) =>
                          handleItemChange(index, "partner_id", e.target.value)
                        }
                        className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                      >
                        <option value="">Chọn đối tác</option>
                        {customerOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-gray-900">
                      <input
                        value={item.reason ?? ""}
                        onChange={(e) =>
                          handleItemChange(index, "reason", e.target.value)
                        }
                        className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                        placeholder="Thu khác"
                      />
                    </td>
                    <td className="px-3 py-2 text-gray-900">
                      <input
                        value={item.description ?? ""}
                        onChange={(e) =>
                          handleItemChange(index, "description", e.target.value)
                        }
                        className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                        placeholder="Chi tiết thu"
                      />
                    </td>
                    <td className="px-3 py-2 text-gray-900">
                      <InputNumber
                        value={item.vat_rate ?? ""}
                        onValueChange={(values) =>
                          handleItemChange(
                            index,
                            "vat_rate",
                            values.floatValue ?? undefined,
                          )
                        }
                        className="h-9 px-2 py-1 text-right text-sm"
                        suffix=" %"
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700 disabled:opacity-50"
                        disabled={items.length === 1}
                      >
                        <Trash2 className="h-4 w-4" />
                        Xóa
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={10}
                    className="px-4 py-6 text-center text-gray-500"
                  >
                    Chưa có dòng chi tiết. Thêm mới để bắt đầu.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-700">
          <span>Tổng cộng</span>
          <span className="font-semibold text-gray-900">
            {totalAmount.toLocaleString("vi-VN")} đ
          </span>
        </div>
      </div>
      </div>
    </>
  );
};

export default ReceiptBankingDetail;
